export const getPlaceSuggestionKey = (place) =>
  `place:${place?.id || place?._id || place?.placeUniqueNumber || place?.contentId || place?.contentid || place?.name || ""}`;

export const getSearchActionKey = (source, term) =>
  `${source}:${String(term || "")}`;

export const getPopularSearchKey = (item) =>
  getSearchActionKey("popular", item?.searchTerm);

const normalizeMatchText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFKC")
    .replace(/[()[\]{}'"`~!@#$%^&*_+=:;,.?/\\|<>-]/g, " ")
    .replace(/\s+/g, "");

const getSearchTokens = (query) =>
  String(query || "")
    .toLowerCase()
    .normalize("NFKC")
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 2);

const EXACT_GENERIC_TERMS = new Set(
  [
    "\uCE74\uD398",
    "\uB9DB\uC9D1",
    "\uC2DD\uB2F9",
    "\uC74C\uC2DD",
    "\uC18C\uACE0\uAE30",
    "\uACE0\uAE30",
    "\uC220\uC9D1",
    "\uB514\uC800\uD2B8",
    "\uBA54\uB274",
    "\uC8FC\uCC28",
    "\uC5EC\uD589",
    "\uB370\uC774\uD2B8",
  ].map(normalizeMatchText)
);

const EXACT_LOCATION_SUFFIXES = [
  "\uC810",
  "\uB3D9",
  "\uAD6C",
  "\uC2DC",
  "\uAD70",
  "\uC74D",
  "\uBA74",
  "\uB85C",
  "\uAE38",
  "\uC5ED",
].map(normalizeMatchText);

const EXACT_NAME_BRANCH_SUFFIXES = [
  ...EXACT_LOCATION_SUFFIXES,
  "\uC9C0\uC810",
  "\uC9C1\uC601\uC810",
  "\uAC00\uB9F9\uC810",
  "\uBCF8\uC810",
  "\uBD84\uC810",
  "\uC5ED\uC810",
  "\uC2DC\uC7A5\uC810",
  "\uACF5\uC6D0\uC810",
  "\uB9C8\uD2B8\uC810",
  "\uBAB0\uC810",
  "\uC13C\uD130\uC810",
].map(normalizeMatchText);

const getTokenVariants = (token) => {
  const normalized = normalizeMatchText(token);
  if (!normalized) return [];

  const variants = new Set([normalized]);
  EXACT_LOCATION_SUFFIXES.forEach((suffix) => {
    if (normalized.endsWith(suffix) && normalized.length > suffix.length + 1) {
      variants.add(normalized.slice(0, -suffix.length));
    }
  });

  return Array.from(variants).filter((variant) => variant.length >= 2);
};

const hasAnyVariant = (text, variants) =>
  variants.some((variant) => text.includes(variant));

const hasNameBranchVariant = (text, variants) =>
  variants.some((variant) =>
    EXACT_NAME_BRANCH_SUFFIXES.some((suffix) => text.includes(`${variant}${suffix}`))
  );

const isGenericSearchToken = (variants) =>
  variants.some((variant) => EXACT_GENERIC_TERMS.has(variant));

const isBranchOrRegionToken = (variants) =>
  variants.some((variant) =>
    EXACT_LOCATION_SUFFIXES.some((suffix) => variant.endsWith(suffix))
  );

export const isHighConfidencePlaceMatch = (query, place) => {
  if (place?.exactPlaceMatch === true) return true;
  if (!query || !place) return false;

  const normalizedQuery = normalizeMatchText(query);
  const nameText = normalizeMatchText(place?.name || place?.title);
  const addressText = normalizeMatchText(
    [place?.address, place?.old_address, place?.city, place?.state]
      .filter(Boolean)
      .join(" ")
  );
  const categoryText = normalizeMatchText(
    [place?.type, place?.category, place?.subcategory].filter(Boolean).join(" ")
  );

  if (
    normalizedQuery.length >= 3 &&
    nameText &&
    !EXACT_GENERIC_TERMS.has(nameText) &&
    (nameText === normalizedQuery ||
      (nameText.length >= 4 && normalizedQuery.includes(nameText)))
  ) {
    return true;
  }

  const tokens = getSearchTokens(query);
  if (tokens.length < 2) return false;

  let hasBrandToken = false;
  let hasRegionOrBranchToken = false;
  let matchedTokenCount = 0;

  tokens.forEach((token) => {
    const variants = getTokenVariants(token);
    const inName = hasAnyVariant(nameText, variants);
    const inAddress = hasAnyVariant(addressText, variants);
    const inCategory = hasAnyVariant(categoryText, variants);
    const generic = isGenericSearchToken(variants);
    const location = isBranchOrRegionToken(variants);
    const nameBranch =
      inName && !generic && hasNameBranchVariant(nameText, variants);

    if (inName || inAddress || inCategory) matchedTokenCount += 1;
    if (inName && !generic && !location && !nameBranch) hasBrandToken = true;
    if (inAddress || (inName && (location || nameBranch))) {
      hasRegionOrBranchToken = true;
    }
  });

  return (
    hasBrandToken &&
    hasRegionOrBranchToken &&
    matchedTokenCount === tokens.length
  );
};

export const buildDesktopSearchNavigationItems = ({
  trimmedSearch,
  popularItems,
  topPlace,
  placeSuggestions,
  matchedRecentTerms,
  suggestionTerms,
  isSuggestionLoading,
}) => {
  if (trimmedSearch.length === 0) {
    return (popularItems || []).map((item) => ({
      key: getPopularSearchKey(item),
      type: "search",
      term: item.searchTerm,
    }));
  }

  const recentTerms = matchedRecentTerms || [];
  const suggestedTerms = suggestionTerms || [];
  const places =
    Array.isArray(placeSuggestions) && placeSuggestions.length > 0
      ? placeSuggestions
      : topPlace
        ? [topPlace]
        : [];
  const items = [];

  places.forEach((place) => {
    items.push({
      key: getPlaceSuggestionKey(place),
      type: "place",
      place,
    });
  });

  if (isSuggestionLoading && places.length === 0) {
    return items;
  }

  recentTerms.forEach((term) => {
    items.push({
      key: getSearchActionKey("recent", term),
      type: "search",
      term,
    });
  });

  suggestedTerms.forEach((term) => {
    items.push({
      key: getSearchActionKey("suggestion", term),
      type: "search",
      term,
    });
  });

  if (
    !isSuggestionLoading &&
    places.length === 0 &&
    recentTerms.length === 0 &&
    suggestedTerms.length === 0
  ) {
    items.push({
      key: getSearchActionKey("fallback", trimmedSearch),
      type: "search",
      term: trimmedSearch,
    });
  }

  return items;
};

export const activateDesktopSearchNavigationItem = (
  item,
  { routeToSearch, routeToBestSearchTarget, routeToPlace }
) => {
  if (!item) return false;

  if (item.type === "place") {
    routeToPlace(item.place);
    return true;
  }

  if (routeToBestSearchTarget) {
    routeToBestSearchTarget(item.term);
  } else {
    routeToSearch(item.term);
  }
  return true;
};
